import React, { useState, useMemo } from 'react';
import { ChevronLeft, Menu } from 'lucide-react';
import { cn } from '../../../lib/utils';

/**
 * Orka App Layouts (Master)
 * =========================
 * Five canonical layouts for the area INSIDE the global sidebar — i.e. an
 * "App Page". Pages can extend beyond the screen but by default are bounded
 * by the viewport with internal scrolling.
 *
 * Variants:
 *   1. AppPageSimple        — small title + body
 *   2. AppPageStack         — title + info + actions + function (stacked)
 *   3. AppPageTabs          — title + actions + tabbed function area
 *   4. AppPageSubNav        — title + collapsible left submenu + body
 *   5. AppPageSubNavTabs    — submenu + tabbed function area (the full thing)
 *
 * All variants are mobile-aware: SubNav becomes a slide-over drawer; tab bars
 * scroll horizontally; action rows wrap; titles compact.
 *
 * These primitives live INSIDE the global app shell (PageLayout). They never
 * include a sidebar themselves — they assume one exists upstream.
 */

// ----------------------------------------------------------------------------
// Shared atoms
// ----------------------------------------------------------------------------

/** Outer container — bounds the page to the viewport with internal scrolling. */
const PageRoot = ({ children, className, testId }) => (
  <div
    data-testid={testId || 'app-page-root'}
    className={cn(
      'flex flex-col min-h-0 h-full overflow-hidden',
      'bg-background',
      className
    )}
  >
    {children}
  </div>
);

/** Small page title block — compact, sits above the body. */
const PageTitle = ({ title, eyebrow, subtitle, className }) => (
  <header
    data-testid="app-page-title"
    className={cn(
      'flex-shrink-0 px-5 sm:px-6 py-3 sm:py-4 border-b border-border',
      className
    )}
  >
    {eyebrow && (
      <div className="text-[10px] uppercase tracking-wider font-semibold orka-text-tertiary mb-0.5">
        {eyebrow}
      </div>
    )}
    <h1 className="text-base sm:text-lg font-semibold orka-text-primary truncate">
      {title}
    </h1>
    {subtitle && (
      <p className="text-xs orka-text-tertiary mt-0.5 line-clamp-2 sm:line-clamp-1">
        {subtitle}
      </p>
    )}
  </header>
);

/** Top "info" strip — narrative / context for the page. */
const InfoSection = ({ children, className }) => (
  <section
    data-testid="app-page-info"
    className={cn(
      'flex-shrink-0 px-5 sm:px-6 py-3 bg-muted/30 border-b border-border text-xs orka-text-secondary',
      className
    )}
  >
    {children}
  </section>
);

/** Action row — buttons / filters / view toggles. */
const ActionsSection = ({ children, className }) => (
  <section
    data-testid="app-page-actions"
    className={cn(
      'flex-shrink-0 px-5 sm:px-6 py-2 border-b border-border bg-background',
      'flex flex-wrap items-center gap-2',
      className
    )}
  >
    {children}
  </section>
);

/** The actual functional content — fills the remaining space, scrolls. */
const FunctionSection = ({ children, padded = true, className }) => (
  <section
    data-testid="app-page-function"
    className={cn(
      'flex-1 min-h-0 overflow-auto',
      padded && 'p-5 sm:p-6',
      className
    )}
  >
    {children}
  </section>
);

/** Horizontal tab bar — scrolls on mobile. */
const TabBar = ({ tabs, value, onChange }) => (
  <nav
    data-testid="app-page-tabs"
    className="flex-shrink-0 border-b border-border bg-background"
  >
    <div className="flex items-center gap-0 overflow-x-auto px-3 sm:px-4">
      {tabs.map((t) => {
        const active = t.value === value;
        return (
          <button
            key={t.value}
            type="button"
            onClick={() => onChange?.(t.value)}
            data-testid={`app-page-tab-${t.value}`}
            className={cn(
              'flex-shrink-0 px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-medium border-b-2 -mb-px transition-colors',
              active
                ? 'border-violet-500 text-violet-600 dark:text-violet-400'
                : 'border-transparent orka-text-tertiary hover:orka-text-primary hover:border-border'
            )}
          >
            {t.icon && <t.icon className="w-3.5 h-3.5 inline-block mr-1.5 -mt-0.5" />}
            {t.label}
            {typeof t.count === 'number' && (
              <span className="ml-1.5 text-[10px] orka-text-tertiary">
                {t.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  </nav>
);

/** Collapsible left submenu inside the page. Mobile = slide-over drawer. */
const SubNav = ({ items, value, onChange, collapsed, onToggleCollapsed }) => (
  <aside
    data-testid="app-page-subnav"
    className={cn(
      'flex-shrink-0 border-r border-border bg-card overflow-y-auto transition-[width] duration-200',
      collapsed ? 'w-12' : 'w-52'
    )}
  >
    <div className="flex items-center justify-between p-2 border-b border-border">
      {!collapsed && (
        <span className="text-[10px] uppercase tracking-wider font-semibold orka-text-tertiary px-1">
          Sections
        </span>
      )}
      <button
        type="button"
        onClick={onToggleCollapsed}
        className="ml-auto p-1.5 rounded hover:bg-muted orka-text-tertiary"
        data-testid="app-page-subnav-toggle"
        title={collapsed ? 'Expand' : 'Collapse'}
      >
        <ChevronLeft className={cn('w-3.5 h-3.5 transition-transform', collapsed && 'rotate-180')} />
      </button>
    </div>
    <ul className="py-1">
      {items.map((it) => {
        const active = it.value === value;
        return (
          <li key={it.value}>
            <button
              type="button"
              onClick={() => onChange?.(it.value)}
              data-testid={`app-page-subnav-item-${it.value}`}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors',
                collapsed && 'justify-center px-1',
                active
                  ? 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-r-2 border-violet-500'
                  : 'orka-text-secondary hover:bg-muted'
              )}
              title={collapsed ? it.label : undefined}
            >
              {it.icon && <it.icon className="w-3.5 h-3.5 flex-shrink-0" />}
              {!collapsed && <span className="truncate">{it.label}</span>}
            </button>
          </li>
        );
      })}
    </ul>
  </aside>
);

/** Mobile slide-over drawer used for SubNav on small screens. */
const MobileDrawer = ({ open, onClose, children }) => (
  <div
    className={cn(
      'fixed inset-0 z-50 md:hidden',
      open ? 'pointer-events-auto' : 'pointer-events-none'
    )}
    data-testid="app-page-mobile-drawer"
  >
    <div
      onClick={onClose}
      className={cn(
        'absolute inset-0 bg-black/40 transition-opacity',
        open ? 'opacity-100' : 'opacity-0'
      )}
    />
    <div
      className={cn(
        'absolute left-0 top-0 bottom-0 w-64 bg-card border-r border-border shadow-xl transition-transform',
        open ? 'translate-x-0' : '-translate-x-full'
      )}
    >
      {children}
    </div>
  </div>
);

// ============================================================================
// VARIANT 1 — Simple
// ============================================================================
export function AppPageSimple({ title, eyebrow, subtitle, children, padded = true, testId = 'app-page-simple' }) {
  return (
    <PageRoot testId={testId}>
      <PageTitle title={title} eyebrow={eyebrow} subtitle={subtitle} />
      <FunctionSection padded={padded}>{children}</FunctionSection>
    </PageRoot>
  );
}

// ============================================================================
// VARIANT 2 — Stack (title + info + actions + function)
// ============================================================================
export function AppPageStack({
  title, eyebrow, subtitle, info, actions, children, padded = true, testId = 'app-page-stack',
}) {
  return (
    <PageRoot testId={testId}>
      <PageTitle title={title} eyebrow={eyebrow} subtitle={subtitle} />
      {info && <InfoSection>{info}</InfoSection>}
      {actions && <ActionsSection>{actions}</ActionsSection>}
      <FunctionSection padded={padded}>{children}</FunctionSection>
    </PageRoot>
  );
}

// ============================================================================
// VARIANT 3 — Tabs (title + actions + tabbed body)
// ============================================================================
export function AppPageTabs({
  title, eyebrow, subtitle, info, actions, tabs, value, onChange, children,
  padded = true, testId = 'app-page-tabs',
}) {
  return (
    <PageRoot testId={testId}>
      <PageTitle title={title} eyebrow={eyebrow} subtitle={subtitle} />
      {info && <InfoSection>{info}</InfoSection>}
      {actions && <ActionsSection>{actions}</ActionsSection>}
      <TabBar tabs={tabs} value={value} onChange={onChange} />
      <FunctionSection padded={padded}>{children}</FunctionSection>
    </PageRoot>
  );
}

// ============================================================================
// VARIANT 4 — SubNav (title + collapsible left submenu + body)
// ============================================================================
export function AppPageSubNav({
  title, eyebrow, subtitle, items, value, onChange, children,
  defaultCollapsed = false, padded = true, testId = 'app-page-subnav-layout',
}) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <PageRoot testId={testId}>
      <header className="flex-shrink-0 px-5 sm:px-6 py-3 sm:py-4 border-b border-border flex items-center gap-2">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="md:hidden p-1.5 rounded hover:bg-muted orka-text-tertiary"
          data-testid="app-page-subnav-mobile-trigger"
          title="Open sections"
        >
          <Menu className="w-4 h-4" />
        </button>
        <div className="min-w-0 flex-1">
          {eyebrow && (
            <div className="text-[10px] uppercase tracking-wider font-semibold orka-text-tertiary mb-0.5">
              {eyebrow}
            </div>
          )}
          <h1 className="text-base sm:text-lg font-semibold orka-text-primary truncate">
            {title}
          </h1>
          {subtitle && (
            <p className="text-xs orka-text-tertiary mt-0.5 line-clamp-1">{subtitle}</p>
          )}
        </div>
      </header>
      <div className="flex-1 min-h-0 flex">
        <div className="hidden md:flex flex-shrink-0">
          <SubNav
            items={items}
            value={value}
            onChange={onChange}
            collapsed={collapsed}
            onToggleCollapsed={() => setCollapsed((c) => !c)}
          />
        </div>
        <FunctionSection padded={padded}>{children}</FunctionSection>
      </div>
      <MobileDrawer open={mobileOpen} onClose={() => setMobileOpen(false)}>
        <SubNav
          items={items}
          value={value}
          onChange={(v) => { onChange?.(v); setMobileOpen(false); }}
          collapsed={false}
          onToggleCollapsed={() => setMobileOpen(false)}
        />
      </MobileDrawer>
    </PageRoot>
  );
}

// ============================================================================
// VARIANT 5 — SubNav + Tabs (full module)
// ============================================================================
export function AppPageSubNavTabs({
  title, eyebrow, subtitle, items, value, onChange,
  tabs, tabValue, onTabChange, info, actions, children,
  defaultCollapsed = false, padded = true, testId = 'app-page-subnav-tabs',
}) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);

  const subNavEl = useMemo(() => (
    <SubNav
      items={items}
      value={value}
      onChange={onChange}
      collapsed={collapsed}
      onToggleCollapsed={() => setCollapsed((c) => !c)}
    />
  ), [items, value, onChange, collapsed]);

  return (
    <PageRoot testId={testId}>
      <header className="flex-shrink-0 px-5 sm:px-6 py-3 sm:py-4 border-b border-border flex items-center gap-2">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="md:hidden p-1.5 rounded hover:bg-muted orka-text-tertiary"
          data-testid="app-page-subnavtabs-mobile-trigger"
          title="Open sections"
        >
          <Menu className="w-4 h-4" />
        </button>
        <div className="min-w-0 flex-1">
          {eyebrow && (
            <div className="text-[10px] uppercase tracking-wider font-semibold orka-text-tertiary mb-0.5">
              {eyebrow}
            </div>
          )}
          <h1 className="text-base sm:text-lg font-semibold orka-text-primary truncate">
            {title}
          </h1>
          {subtitle && (
            <p className="text-xs orka-text-tertiary mt-0.5 line-clamp-1">{subtitle}</p>
          )}
        </div>
      </header>
      <div className="flex-1 min-h-0 flex">
        <div className="hidden md:flex flex-shrink-0">{subNavEl}</div>
        <div className="flex-1 min-w-0 flex flex-col min-h-0">
          {info && <InfoSection>{info}</InfoSection>}
          {actions && <ActionsSection>{actions}</ActionsSection>}
          {tabs?.length > 0 && <TabBar tabs={tabs} value={tabValue} onChange={onTabChange} />}
          <FunctionSection padded={padded}>{children}</FunctionSection>
        </div>
      </div>
      <MobileDrawer open={mobileOpen} onClose={() => setMobileOpen(false)}>
        <SubNav
          items={items}
          value={value}
          onChange={(v) => { onChange?.(v); setMobileOpen(false); }}
          collapsed={false}
          onToggleCollapsed={() => setMobileOpen(false)}
        />
      </MobileDrawer>
    </PageRoot>
  );
}

// Named bundle for easier discovery from showcases.
export const AppPageVariants = {
  Simple: AppPageSimple,
  Stack: AppPageStack,
  Tabs: AppPageTabs,
  SubNav: AppPageSubNav,
  SubNavTabs: AppPageSubNavTabs,
};

export default AppPageVariants;
